import React from 'react'
import moment from 'moment-timezone'
import {
    IAbstractNativeApnsSendIntegration,
    IWebApnsSendIntegration,
    SendIntegrationChannel,
} from '../../interfaces/send-integrations'
import { SendIntegrationPrivateKeyType } from '../../enums/send-integration-private-key-type.enum'
import {
    AbstractNativeApnsConfiguration,
    NativeP12ApnsConfiguration,
    NativeP8ApnsConfiguration,
} from '../../models/send-integration-configurations/native-apns-configurations.model'
import { WebApnsConfigurationModel } from '../../models/send-integration-configurations/web-apns-configuration.model'
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'
import { simpleNotification, titleCase } from '../../_utils/utils'
import { SHORT_DATE_FORMAT } from '../../constants'
import { Flex, Form, Space, Typography, notification, theme, ConfigProvider } from 'antd'
import CopyToClipboard from 'react-copy-to-clipboard'
import { getClassNames } from '../../_utils/classnames'
import { SETTINGS_WELL_FORM_LAYOUT } from '../../theme/constants'

type WebApnsProps = {
    channel: SendIntegrationChannel.WEB
    apnsConfiguration: IWebApnsSendIntegration
}

type NativeApnsProps = {
    channel: SendIntegrationChannel.NATIVE
    apnsConfiguration: IAbstractNativeApnsSendIntegration
}

type ApnsIntegrationWellProps = WebApnsProps | NativeApnsProps

export const ApnsIntegrationWell = (props: ApnsIntegrationWellProps) => {
    let configClass:
        | typeof NativeP8ApnsConfiguration
        | typeof NativeP12ApnsConfiguration
        | typeof WebApnsConfigurationModel
        | undefined
    if (props.channel === SendIntegrationChannel.WEB) {
        configClass = WebApnsConfigurationModel
    } else if (props.apnsConfiguration.privateKeyType === SendIntegrationPrivateKeyType.P8) {
        configClass = NativeP8ApnsConfiguration
    } else if (props.apnsConfiguration.privateKeyType === SendIntegrationPrivateKeyType.P12) {
        configClass = NativeP12ApnsConfiguration
    }

    const config = configClass?.build(props.apnsConfiguration)
    const enabled = config?.getIsActive() ?? false

    const isNativeView = props.channel === SendIntegrationChannel.NATIVE
    const firstSectionRenderValue =
        config instanceof WebApnsConfigurationModel
            ? config?.getPushPackage()?.website_push_id!
            : config?.getAppBundleId()!
    const handleCopied = React.useCallback((field: string) => {
        notification.destroy()
        simpleNotification('success', `Your ${field} has been copied!`)
    }, [])

    const { token } = theme.useToken()

    return (
        <Flex vertical gap={8} className="web-apns-well">
            <Form {...SETTINGS_WELL_FORM_LAYOUT} colon={false} requiredMark={false} labelAlign="left" size="small">
                <Space size={6} className="integrations-section-title">
                    <span className={enabled ? 'enabled' : 'disabled'}>
                        {enabled ? (
                            <CheckCircleOutlined style={{ color: token.colorSuccess }} />
                        ) : (
                            <CloseCircleOutlined style={{ color: token.colorError }} />
                        )}
                    </span>
                    <Typography.Text>{props.channel}</Typography.Text>
                </Space>

                {!enabled ? (
                    <Form.Item label="Status">
                        <Typography.Text type="secondary">Not Enabled</Typography.Text>
                    </Form.Item>
                ) : (
                    <>
                        {!(config instanceof NativeP8ApnsConfiguration) && (
                            <>
                                <Form.Item label={isNativeView ? 'App Bundle ID' : 'Website Push Id'}>
                                    <span className={getClassNames('can-copy')}>
                                        <CopyToClipboard
                                            onCopy={() =>
                                                handleCopied(isNativeView ? 'app bundle id' : 'website push id')
                                            }
                                            text={firstSectionRenderValue ?? ''}
                                        >
                                            <Typography.Text>{firstSectionRenderValue}</Typography.Text>
                                        </CopyToClipboard>
                                    </span>
                                </Form.Item>

                                <Form.Item label="Expiration">
                                    <Typography.Text>
                                        {moment(config?.getExpiration()).format(SHORT_DATE_FORMAT)}
                                    </Typography.Text>
                                </Form.Item>
                            </>
                        )}

                        {config instanceof NativeP8ApnsConfiguration && (
                            <>
                                <Form.Item label="Team ID">
                                    <Typography.Text>{config.getTeamId()}</Typography.Text>
                                </Form.Item>

                                <Form.Item label="Key ID">
                                    <Typography.Text>{config.getKeyId()}</Typography.Text>
                                </Form.Item>
                            </>
                        )}

                        {config instanceof AbstractNativeApnsConfiguration && (
                            <Form.Item label="Environment">
                                <Typography.Text>
                                    {`${titleCase(config.getApnsEnvironment())}${
                                        !config.isProductionApnsEnvironment ? ' (sandbox)' : ''
                                    }`}
                                </Typography.Text>
                            </Form.Item>
                        )}
                    </>
                )}
            </Form>
        </Flex>
    )
}
